import { Injectable } from '@angular/core';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

/**
 * Copies an agent reply to the clipboard in BOTH flavours:
 *  - `text/html`  — so rich targets (Teams, Outlook, Word, Confluence) paste the
 *                   reply already formatted: bold, bullets, headings, tables.
 *  - `text/plain` — the original markdown, for editors and terminals.
 *
 * Without the HTML flavour a paste into Teams shows the literal `**bold**` and
 * `-` bullets, which is what users see when copying raw markdown.
 *
 * The HTML is rendered with marked's DEFAULT renderer on purpose — the chat's
 * MarkdownPipe renderer injects code-block headers with a "Copy" button and
 * turns links into citation pills (favicon img + tooltip spans). That markup is
 * chat chrome; pasting it into Teams would carry a stray "Copy" word and broken
 * pill fragments, so the clipboard gets clean semantic HTML instead.
 */
@Injectable({ providedIn: 'root' })
export class RichCopyService {

  /** Markdown → sanitised, presentation-neutral HTML for the clipboard. */
  private toHtml(markdown: string): string {
    const rawHtml = marked.parse(markdown, { async: false }) as string;
    return DOMPurify.sanitize(rawHtml, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'ul', 'ol', 'li',
                     'table', 'thead', 'tbody', 'tr', 'th', 'td', 'code',
                     'pre', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote',
                     'a', 'hr', 's', 'del'],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
      FORCE_BODY: true,
    });
  }

  /**
   * Writes the markdown to the clipboard as rich text + plain text.
   * Rejects when the clipboard is unavailable (insecure context, denied
   * permission) so callers can surface their own error.
   */
  async copyMarkdown(markdown: string): Promise<void> {
    const text = markdown.trim();
    if (!text) return;

    // Older browsers (and Firefox before 127) have no clipboard.write /
    // ClipboardItem — those fall back to markdown-only, same as before.
    const canWriteRich = typeof ClipboardItem !== 'undefined'
      && typeof navigator.clipboard?.write === 'function';

    if (canWriteRich) {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([this.toHtml(text)], { type: 'text/html' }),
            'text/plain': new Blob([text], { type: 'text/plain' }),
          }),
        ]);
        return;
      } catch {
        // Fall through: some browsers reject the html flavour outright.
      }
    }

    await navigator.clipboard.writeText(text);
  }
}
